import APP_ASSETS from "@/assets";
import { DrawerClose, DrawerFooter } from "@/components/ui/drawer";
import { useOrdersWrite } from "@/hooks";
import { Order, OrderStatus, OrderType } from "@/models";
import { useEffect, useMemo, useState } from "react";
import { useToast } from "./ui/use-toast";
import { useMerchant, useOrders } from "@/contexts";
import { Button } from "./ui/button/button";
import { LoadingSpinner } from ".";
import { ethers } from "ethers";
import { ButtonType } from "./ui/button/button.type";
import { Skeleton } from "./ui/skeleton";
import { getMerchantUpi } from "@/helper/localstorage";
import { copyToClipboard, upiIdDisplay } from "@/lib/utils";
import { trackAmplitudeEvent } from "@/utils/amplitude";

interface CompleteOrderDrawerProps {
  order: Order;
  onClose: () => void;
  isLoading: boolean;
  isExpire: boolean;
}

const CompleteOrderDrawer = ({
  order,
  onClose,
  isLoading,
  isExpire,
}: CompleteOrderDrawerProps) => {
  const [encUpiId, setEncUpiId] = useState<string | null>(null);
  const { toast } = useToast();
  const { refreshOrders } = useOrders();
  const {
    merchantDetails,
    hasFetched: fetchedMerchantDetails,
    isLoading: fetchingMerchantDetails,
  } = useMerchant();
  const { completeOrder } = useOrdersWrite();

  const [isCompleting, setIsCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);

  const hasCompleted = useMemo(
    () => completed || order.status === OrderStatus.COMPLETED,
    [completed, order.status],
  );

  useEffect(() => {
    (async () => {
      const upi = await getMerchantUpi();
      if (upi) {
        setEncUpiId(upi);
      }
    })();
  }, []);

  const handleCompleteOrder = async () => {
    if (order.orderType === OrderType.SELL && order.encUpi === "") {
      toast({
        title: "UPI ID not found",
        description: "Please wait for the buyer to provide UPI ID.",
        variant: "destructive",
      });
      return;
    }
    setIsCompleting(true);
    const result = await completeOrder(order.id);
    //@ts-ignore
    if (result?.afterWait?.status === "success") {
      trackAmplitudeEvent("ORDER_COMPLETED");
      setCompleted(true);
      toast({
        title: "Order Completed",
        description: `Order ${order.id} status changed to COMPLETED.`,
      });
    } else {
      trackAmplitudeEvent("ORDER_COMPLETION_FAILED");
      toast({
        title: "Order wasn't Completed",
        description: result.message,
        variant: "destructive",
      });
    }
    setIsCompleting(false);
    onClose();
    refreshOrders();
  };

  return (
    <>
      <section className="mx-4 flex flex-col justify-center gap-2">
        <div className="flex flex-col justify-center gap-1">
          <label
            style={{
              fontWeight: "900",
            }}
            className={`font-satoshiBold w-fit rounded p-2 text-xs tracking-wide text-text-dark ${
              order.orderType === OrderType.BUY
                ? "bg-green-500"
                : "bg-yellow-500"
            }`}
            htmlFor="address">
            {order.orderType === OrderType.SELL ? "SEND" : "RECEIVE"}
          </label>
          <p className="text-2xl font-bold text-text-light">
            ₹{" "}
            {Math.round(Number(ethers.utils.formatUnits(order.fiatAmount, 6)))
              .toFixed(2)
              .toString()}
          </p>
        </div>
        <div className="flex flex-col justify-center gap-1">
          {order?.encUpi && upiIdDisplay(order?.encUpi)?.tempQr ? null : (
            <label
              className="font-satoshiBold text-xs tracking-wide text-text-light"
              htmlFor="address">
              TO {order.orderType === OrderType.SELL ? "THIS" : "YOUR"} UPI ID
            </label>
          )}
          <div className="flex flex-wrap items-center justify-start gap-4 overflow-x-auto">
            {order?.encUpi && upiIdDisplay(order?.encUpi)?.tempQr ? (
              <a
                href={upiIdDisplay(order?.encUpi)?.qrCode}
                target="_blank"
                className={`w-full rounded-lg bg-primary py-2 text-center text-lg font-medium text-black ${
                  isExpire ? "cursor-not-allowed opacity-50" : ""
                }`}
                onClick={(e) => {
                  if (isExpire) {
                    e.preventDefault();
                  } else {
                    trackAmplitudeEvent("UPI_QR_PAYMENT_CLICKED");
                  }
                }}
                rel="noreferrer">
                Click to Pay
              </a>
            ) : (
              <p className="text-2xl font-bold text-text-light">
                {order.orderType === OrderType.BUY ? (
                  !fetchingMerchantDetails &&
                  fetchedMerchantDetails &&
                  merchantDetails ? (
                    encUpiId
                  ) : (
                    <LoadingSpinner className="text-text-primary" />
                  )
                ) : order?.encUpi ? (
                  upiIdDisplay(order?.encUpi)?.extractedUpi
                ) : (
                  <LoadingSpinner className="text-text-primary" />
                )}
              </p>
            )}
            {order.orderType === OrderType.SELL &&
            order?.encUpi &&
            !upiIdDisplay(order?.encUpi)?.tempQr ? (
              <img
                className="color-black h-7 w-7 hover:cursor-pointer"
                src={APP_ASSETS.ICONS.PASTE}
                alt={APP_ASSETS.ICONS.PASTE_BLACK}
                onClick={() => {
                  if (order.encUpi) {
                    trackAmplitudeEvent("UPI_ID_COPIED");
                    copyToClipboard(upiIdDisplay(order.encUpi)?.extractedUpi);
                    toast({
                      title: "Upi Copied",
                    });
                  }
                }}
              />
            ) : null}
          </div>
        </div>
      </section>
      <DrawerFooter>
        {isLoading ? (
          <Skeleton className="h-[44px] w-full bg-bg-grey" />
        ) : (
          <>
            {!hasCompleted ? (
              <Button
                label={`${
                  order.orderType === OrderType.BUY
                    ? "I have received this payment"
                    : "I have made this payment"
                }`}
                disabled={
                  (order.orderType === OrderType.BUY &&
                    order.status !== OrderStatus.PAID) ||
                  (order.orderType === OrderType.SELL && order?.encUpi == "")
                }
                loading={isCompleting}
                type={ButtonType.primary}
                onClick={handleCompleteOrder}
                widthFull
              />
            ) : (
              "Order Completed, you can close this now."
            )}
          </>
        )}
        <DrawerClose
          onClick={() => {
            onClose?.();
          }}>
          <div className="w-full rounded-md border-[1px] border-black bg-bg-secondary p-2 font-bold text-text-light">
            Close
          </div>
        </DrawerClose>
      </DrawerFooter>
    </>
  );
};

export default CompleteOrderDrawer;
